<template>
  <div class="coupon-setting">
    <div class="setting-row">
      <span class="setting-label">添加优惠券</span>
      <el-radio-group v-model="$$FormData.coupon_mode" size="mini" @change="handleModeChange">
        <el-radio label="manual">手动添加</el-radio>
        <el-radio label="auto">自动获取</el-radio>
      </el-radio-group>
    </div>

    <template v-if="$$FormData.coupon_mode === 'manual'">
      <el-button class="add-button" type="text" @click="openPicker">＋ 添加优惠券</el-button>
      <div v-if="selectedCoupons.length" class="selected-list">
        <div v-for="coupon in selectedCoupons" :key="coupon.coupon_id" class="selected-item">
          <span class="selected-title">{{ coupon.title }}</span>
          <span class="selected-value">{{ couponValue(coupon) }}</span>
          <el-tooltip v-if="couponUnavailableReason(coupon)" :content="couponUnavailableReason(coupon)" placement="top">
            <span class="unavailable-tag">不可领取</span>
          </el-tooltip>
          <i class="el-icon-close" @click="removeCoupon(coupon.coupon_id)"></i>
        </div>
      </div>
    </template>
    <p v-else class="auto-tip">自动展示当前装修主体可领取且未领完的优惠券。</p>

    <div class="divider"></div>
    <div class="setting-row">
      <span class="setting-label">排列样式</span>
      <strong>{{ layoutLabel }}</strong>
    </div>
    <div class="layout-list">
      <button
        v-for="item in layouts"
        :key="item.value"
        type="button"
        :title="item.label"
        :class="{ active: $$FormData.layout === item.value }"
        @click="$$FormData.layout = item.value"
      ><i :class="item.icon"></i></button>
    </div>

    <div class="setting-row card-style-title">
      <span class="setting-label">卡片样式</span>
      <strong>样式{{ styleNumber }}</strong>
    </div>
    <div class="style-list">
      <button
        v-for="index in 8"
        :key="index"
        type="button"
        class="style-option"
        :class="[{ active: $$FormData.card_style === `style-${index}` }, `style-${index}`]"
        @click="$$FormData.card_style = `style-${index}`"
      ><span></span></button>
    </div>

    <div class="setting-row color-row">
      <span class="setting-label">颜色</span>
      <strong>{{ colorName }}</strong>
      <button
        v-for="color in colors"
        :key="color"
        type="button"
        class="color-dot"
        :class="{ active: String($$FormData.theme_color).toLowerCase() === color }"
        :style="{ backgroundColor: color }"
        @click="$$FormData.theme_color = color"
      ></button>
      <el-color-picker v-model="$$FormData.theme_color" size="mini" :predefine="colors" />
    </div>
    <div class="setting-row">
      <span class="setting-label wide">隐藏已抢完及失效的券</span>
      <strong>{{ $$FormData.hide_unavailable ? '隐藏' : '显示' }}</strong>
      <el-checkbox v-model="$$FormData.hide_unavailable"></el-checkbox>
    </div>
    <p class="setting-tip">当页面无展示的优惠券时，优惠券区域将隐藏。</p>

    <el-dialog title="添加优惠券" :visible.sync="pickerVisible" width="760px" append-to-body>
      <div class="picker-toolbar">
        <el-input v-model="keyword" placeholder="请输入优惠券名称" clearable size="small" @keyup.enter.native="loadCoupons" />
        <el-button type="primary" size="small" @click="loadCoupons">搜索</el-button>
      </div>
      <el-table v-loading="loading" :data="coupons" height="360" @selection-change="handleSelectionChange">
        <el-table-column type="selection" width="46" :selectable="isCouponSelectable" />
        <el-table-column prop="title" label="优惠券名称" min-width="180" show-overflow-tooltip />
        <el-table-column label="优惠内容" width="120">
          <template slot-scope="scope">{{ couponValue(scope.row) }}</template>
        </el-table-column>
        <el-table-column label="使用门槛" width="130">
          <template slot-scope="scope">{{ thresholdText(scope.row) }}</template>
        </el-table-column>
        <el-table-column label="有效期" width="190">
          <template slot-scope="scope">{{ dateText(scope.row) }}</template>
        </el-table-column>
        <el-table-column label="领取状态" width="120">
          <template slot-scope="scope">
            <span :class="couponUnavailableReason(scope.row) ? 'status-invalid' : 'status-valid'">
              {{ couponUnavailableReason(scope.row) || '可领取' }}
            </span>
          </template>
        </el-table-column>
      </el-table>
      <el-pagination
        class="pagination"
        background
        layout="total, prev, pager, next"
        :current-page="pageNo"
        :page-size="pageSize"
        :total="total"
        @current-change="changePage"
      />
      <span slot="footer">
        <el-button @click="pickerVisible = false">取消</el-button>
        <el-button type="primary" @click="confirmSelection">确定</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { getDecorCoupons } from '@/api/coupon'
import { couponUnavailableReason } from '@/utils/coupon-claim'

export default {
  name: 'CouponSettingSelector',
  data() {
    return {
      pickerVisible: false,
      loading: false,
      coupons: [],
      pendingSelection: [],
      keyword: '',
      pageNo: 1,
      pageSize: 10,
      total: 0,
      colors: ['#ff4d35', '#ffbd45', '#eeeeee', '#303133', '#dff3e8'],
      layouts: [
        { label: '一行一个', value: 'single', icon: 'el-icon-picture' },
        { label: '一行两个', value: 'two', icon: 'el-icon-menu' },
        { label: '一行三个', value: 'three', icon: 'el-icon-s-grid' },
        { label: '横向滑动', value: 'horizontal', icon: 'el-icon-s-operation' }
      ]
    }
  },
  computed: {
    selectedCoupons() {
      return Array.isArray(this.$$FormData.coupon_list) ? this.$$FormData.coupon_list : []
    },
    styleNumber() {
      return String(this.$$FormData.card_style || 'style-3').replace('style-', '')
    },
    layoutLabel() {
      const item = this.layouts.find(layout => layout.value === this.$$FormData.layout)
      return item ? item.label : '横向滑动'
    },
    colorName() {
      const color = String(this.$$FormData.theme_color || '').toLowerCase()
      return ({ '#ff4d35': '红色', '#ffbd45': '黄色', '#eeeeee': '灰色', '#303133': '黑色', '#dff3e8': '绿色' })[color] || '自定义'
    }
  },
  created() {
    this.ensureDefaults()
    if (this.$$FormData.coupon_mode === 'auto') this.loadAutoCoupons()
  },
  methods: {
    ensureDefaults() {
      const defaults = { coupon_mode: 'manual', coupon_ids: [], coupon_list: [], layout: 'horizontal', card_style: 'style-3', theme_color: '#ff4d35', hide_unavailable: true }
      Object.keys(defaults).forEach(key => {
        if (!Object.prototype.hasOwnProperty.call(this.$$FormData, key)) this.$set(this.$$FormData, key, defaults[key])
      })
    },
    handleModeChange(mode) {
      if (mode === 'auto') this.loadAutoCoupons()
    },
    async loadAutoCoupons() {
      try {
        const response = await getDecorCoupons({ page_no: 1, page_size: 100 })
        const coupons = ((response && response.data) || []).filter(this.isClaimable)
        this.$set(this.$$FormData, 'coupon_list', coupons)
      } catch (error) {
        // 请求层已经提示错误，保留上次预览数据。
      }
    },
    openPicker() {
      this.pickerVisible = true
      this.pageNo = 1
      this.loadCoupons()
    },
    async loadCoupons() {
      this.loading = true
      try {
        const response = await getDecorCoupons({ page_no: this.pageNo, page_size: this.pageSize, keyword: this.keyword || undefined })
        // 手动选择必须展示接口返回的完整列表；是否过期、领完由展示开关处理。
        // 后端历史数据的 manual_claim_enabled 可能序列化为 true/1，不能在分页后再次严格过滤，
        // 否则会出现“总数有数据、当前页为空”的假象。
        this.coupons = (response && response.data) || []
        this.total = Number((response && response.data_total) || 0)
      } finally {
        this.loading = false
      }
    },
    changePage(page) {
      this.pageNo = page
      this.loadCoupons()
    },
    handleSelectionChange(rows) {
      this.pendingSelection = rows || []
    },
    confirmSelection() {
      const merged = new Map(this.selectedCoupons.map(coupon => [String(coupon.coupon_id), coupon]))
      this.pendingSelection.forEach(coupon => merged.set(String(coupon.coupon_id), Object.assign({}, coupon)))
      const mergedList = Array.from(merged.values())
      const list = mergedList.filter(this.isCouponSelectable).slice(0, 50)
      this.$set(this.$$FormData, 'coupon_list', list)
      this.$set(this.$$FormData, 'coupon_ids', list.map(coupon => coupon.coupon_id))
      this.pickerVisible = false
      if (list.length < mergedList.length) this.$message.warning('已移除不支持买家领取、失效或已领完的优惠券')
    },
    removeCoupon(id) {
      const list = this.selectedCoupons.filter(coupon => String(coupon.coupon_id) !== String(id))
      this.$set(this.$$FormData, 'coupon_list', list)
      this.$set(this.$$FormData, 'coupon_ids', list.map(coupon => coupon.coupon_id))
    },
    couponValue(coupon) {
      return coupon.discount_type === 'DISCOUNT' ? `${coupon.coupon_price}折` : `¥${coupon.coupon_price}`
    },
    thresholdText(coupon) {
      const value = Number(coupon.coupon_threshold_price || 0)
      return value > 0 ? `满${value}元可用` : '无门槛'
    },
    dateText(coupon) {
      if (coupon.validity_type === 'IMMEDIATE') return `领取后${coupon.valid_days || 0}天有效`
      if (coupon.validity_type === 'DELAYED') return `领取${coupon.effective_delay_days || 0}天后生效`
      const format = timestamp => timestamp ? new Date(Number(timestamp) * 1000).toLocaleDateString() : '--'
      return `${format(coupon.start_time)} - ${format(coupon.end_time)}`
    },
    isClaimable(coupon) {
      return !this.couponUnavailableReason(coupon)
    },
    isCouponSelectable(coupon) {
      return !this.couponUnavailableReason(coupon)
    },
    couponUnavailableReason(coupon) {
      return couponUnavailableReason(coupon)
    }
  }
}
</script>

<style scoped>
.coupon-setting { padding: 16px 12px; color: #606266; }
.setting-row { display: flex; align-items: center; min-height: 38px; gap: 12px; }
.setting-label { min-width: 82px; color: #909399; }
.setting-label.wide { min-width: 170px; }
.add-button { width: 100%; margin: 10px 0; padding: 10px; border: 1px solid #409eff; }
.auto-tip,.setting-tip { margin: 8px 0; color: #a0a3aa; font-size: 12px; line-height: 1.6; }
.selected-list { max-height: 180px; overflow: auto; border: 1px solid #ebeef5; }
.selected-item { display: flex; align-items: center; gap: 8px; padding: 9px; border-bottom: 1px solid #ebeef5; }
.selected-title { flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.selected-value { color: #f56c6c; }
.unavailable-tag,.status-invalid { color: #f56c6c; font-size: 12px; }
.status-valid { color: #67c23a; }
.selected-item i { cursor: pointer; }
.divider { height: 1px; margin: 16px 0; background: #ebeef5; }
.layout-list { display: flex; margin: 2px 0 14px; }
.layout-list button { width: 38px; height: 28px; color: #909399; background: #f4f5f6; border: 1px solid #dcdfe6; cursor: pointer; }
.layout-list button.active { position: relative; z-index: 1; color: #409eff; border-color: #409eff; }
.card-style-title { margin-top: 4px; }
.style-list { display: grid; grid-template-columns: repeat(6, 34px); gap: 6px; margin: 4px 0 15px; }
.style-option { height: 26px; padding: 5px; background: #f4f5f6; border: 1px solid transparent; cursor: pointer; }
.style-option.active { border-color: #409eff; background: #eef6ff; }
.style-option span { display: block; width: 100%; height: 100%; border: 2px solid #a7abb2; box-sizing: border-box; }
.style-option.style-2 span,.style-option.style-5 span { border-radius: 5px; }
.style-option.style-3 span,.style-option.style-6 span { border-left-width: 6px; }
.style-option.style-4 span,.style-option.style-8 span { border-style: dashed; }
.style-option.style-7 span { border-radius: 50%; }
.color-row { gap: 8px; }
.color-row /deep/ .el-color-picker { margin-left: 2px; }
.color-dot { width: 18px; height: 18px; padding: 0; border: 2px solid #fff; border-radius: 50%; box-shadow: 0 0 0 1px #dcdfe6; cursor: pointer; }
.color-dot.active { box-shadow: 0 0 0 2px #409eff; }
.picker-toolbar { display: flex; gap: 8px; margin-bottom: 12px; }
.pagination { margin-top: 14px; text-align: right; }
</style>
